import { defineField, defineType } from 'sanity';

export default defineType({
  name: 'caseStudy',
  title: 'Case Study',
  type: 'document',
  description:
    'Proof of work. Replaces the handyman-era before/after gallery and neighborhood pages.',
  fields: [
    defineField({
      name: 'title',
      title: 'Title',
      type: 'string',
      description:
        'Outcome-led, e.g. "Cut invoice processing from 6 days to 4 hours"',
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'slug',
      title: 'Slug',
      type: 'slug',
      options: { source: 'title', maxLength: 96 },
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'summary',
      title: 'Summary',
      type: 'text',
      description: 'Card blurb (2-3 sentences)',
      validation: Rule => Rule.required().max(300),
    }),

    // --- Client ---
    defineField({
      name: 'confidential',
      title: 'Confidential Engagement',
      type: 'boolean',
      description:
        'Hide the client name publicly and show the anonymous label instead',
      initialValue: false,
    }),
    defineField({
      name: 'clientName',
      title: 'Client Name',
      type: 'string',
      hidden: ({ parent }) => parent?.confidential,
    }),
    defineField({
      name: 'clientLabel',
      title: 'Anonymous Client Label',
      type: 'string',
      description:
        'Used when confidential, e.g. "A regional logistics operator"',
      hidden: ({ parent }) => !parent?.confidential,
    }),
    defineField({
      name: 'clientLogo',
      title: 'Client Logo',
      type: 'image',
      options: { hotspot: true },
      hidden: ({ parent }) => parent?.confidential,
    }),
    defineField({
      name: 'industry',
      title: 'Industry',
      type: 'string',
      description: 'e.g. Logistics, Healthcare, Field Services, SaaS',
    }),

    // --- Narrative ---
    defineField({
      name: 'featuredImage',
      title: 'Featured Image',
      type: 'image',
      options: { hotspot: true },
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'challenge',
      title: 'The Challenge',
      type: 'text',
      description: 'What was broken or blocked before the engagement',
      validation: Rule => Rule.required().min(40),
    }),
    defineField({
      name: 'approach',
      title: 'Our Approach',
      type: 'array',
      of: [{ type: 'string' }],
      description: '3-6 points on how the problem was tackled',
      validation: Rule => Rule.required().min(3).max(6),
    }),
    defineField({
      name: 'solution',
      title: 'What We Built',
      type: 'array',
      of: [
        {
          type: 'block',
          styles: [
            { title: 'Normal', value: 'normal' },
            { title: 'H2', value: 'h2' },
            { title: 'H3', value: 'h3' },
          ],
        },
        { type: 'image', options: { hotspot: true } },
      ],
    }),
    defineField({
      name: 'outcomes',
      title: 'Outcomes',
      type: 'array',
      description:
        'Quantified results. These are the most persuasive part of the page.',
      of: [
        {
          type: 'object',
          fields: [
            defineField({
              name: 'metric',
              title: 'Metric',
              type: 'string',
              description: 'e.g. "Invoice turnaround"',
              validation: Rule => Rule.required(),
            }),
            defineField({
              name: 'value',
              title: 'Value',
              type: 'string',
              description: 'e.g. "6 days → 4 hours" or "−73%"',
              validation: Rule => Rule.required(),
            }),
            defineField({
              name: 'description',
              title: 'Context',
              type: 'text',
              validation: Rule => Rule.max(200),
            }),
          ],
          preview: {
            select: { title: 'value', subtitle: 'metric' },
          },
        },
      ],
      validation: Rule => Rule.max(6),
    }),

    // --- Engagement facts ---
    defineField({
      name: 'techStack',
      title: 'Tech Stack',
      type: 'array',
      of: [{ type: 'string' }],
      options: { layout: 'tags' },
      description: 'e.g. Next.js, Postgres, AWS Lambda, Claude API',
    }),
    defineField({
      name: 'services',
      title: 'Related Services',
      type: 'array',
      of: [{ type: 'reference', to: [{ type: 'service' }] }],
    }),
    defineField({
      name: 'engagementLength',
      title: 'Engagement Length',
      type: 'string',
      description: 'e.g. "6 weeks", "Ongoing retainer"',
    }),
    defineField({
      name: 'completionDate',
      title: 'Completion Date',
      type: 'date',
      validation: Rule => Rule.required(),
    }),

    // --- Media & proof ---
    defineField({
      name: 'liveUrl',
      title: 'Live Link',
      type: 'url',
      description:
        'A working version visitors can open, e.g. a public demo or gallery. Shown as a button on the case study and a link on its card.',
      validation: Rule => Rule.uri({ scheme: ['https', 'http'] }),
    }),
    defineField({
      name: 'liveUrlLabel',
      title: 'Live Link Label',
      type: 'string',
      description: 'Button text, e.g. "Explore the live design system"',
      hidden: ({ parent }) => !parent?.liveUrl,
      validation: Rule => Rule.max(40),
    }),
    defineField({
      name: 'screenshots',
      title: 'Screenshots',
      type: 'array',
      of: [
        {
          type: 'image',
          options: { hotspot: true },
          fields: [
            defineField({ name: 'caption', title: 'Caption', type: 'string' }),
            defineField({
              name: 'alt',
              title: 'Alt Text',
              type: 'string',
              validation: Rule => Rule.max(125),
            }),
          ],
        },
      ],
    }),
    defineField({
      name: 'testimonial',
      title: 'Testimonial',
      type: 'reference',
      to: [{ type: 'testimonial' }],
    }),

    // --- SEO & display ---
    defineField({
      name: 'seoTitle',
      title: 'SEO Title',
      type: 'string',
    }),
    defineField({
      name: 'seoDescription',
      title: 'SEO Description',
      type: 'text',
      validation: Rule => Rule.max(160),
    }),
    defineField({
      name: 'featured',
      title: 'Featured',
      type: 'boolean',
      initialValue: false,
    }),
    defineField({
      name: 'showOnHomepage',
      title: 'Show on Homepage',
      type: 'boolean',
      initialValue: false,
    }),
    defineField({
      name: 'sortOrder',
      title: 'Sort Order',
      type: 'number',
      initialValue: 100,
    }),
    defineField({
      name: 'published',
      title: 'Published',
      type: 'boolean',
      initialValue: true,
    }),
  ],
  orderings: [
    {
      title: 'Completion Date (Newest)',
      name: 'completionDateDesc',
      by: [{ field: 'completionDate', direction: 'desc' }],
    },
    {
      title: 'Featured & Sort Order',
      name: 'featuredAndSort',
      by: [
        { field: 'featured', direction: 'desc' },
        { field: 'sortOrder', direction: 'asc' },
      ],
    },
  ],
  preview: {
    select: {
      title: 'title',
      clientName: 'clientName',
      clientLabel: 'clientLabel',
      confidential: 'confidential',
      industry: 'industry',
      media: 'featuredImage',
      featured: 'featured',
      showOnHomepage: 'showOnHomepage',
      published: 'published',
    },
    prepare(selection) {
      const {
        title,
        clientName,
        clientLabel,
        confidential,
        industry,
        media,
        featured,
        showOnHomepage,
        published,
      } = selection;

      const client = confidential
        ? clientLabel || 'Confidential client'
        : clientName || 'No client';

      const badges = [];
      if (featured) badges.push('⭐');
      if (showOnHomepage) badges.push('🏠');
      if (confidential) badges.push('🔒');
      if (!published) badges.push('(Draft)');

      return {
        title: `${title}${badges.length ? ` ${badges.join(' ')}` : ''}`,
        subtitle: `${client}${industry ? ` • ${industry}` : ''}`,
        media,
      };
    },
  },
});
